
import React, { useEffect, useState } from 'react';
import { CheckCircleIcon } from './icons/CheckCircleIcon';
import { CreditCardIcon } from './icons/CreditCardIcon';
import { XIcon } from './icons/XIcon';
import { sendPaymentEmail } from '../services/emailService';
import { ShieldIcon } from './icons/ShieldIcon';
import { XCircleIcon } from './icons/XCircleIcon';

interface PaymentConfirmationModalProps {
    onClose: () => void;
    userEmail?: string | null;
    isTestMode: boolean;
    isAlreadyPaid?: boolean;
    onPaymentSuccess?: () => void;
}

const PaymentConfirmationModal: React.FC<PaymentConfirmationModalProps> = ({ onClose, userEmail, isTestMode, isAlreadyPaid = false, onPaymentSuccess }) => {
    const [configError, setConfigError] = useState(false);
    const [isAgreed, setIsAgreed] = useState(false);
    
    useEffect(() => {
        // Only send receipt email if payment is actually confirmed
        if (isAlreadyPaid && userEmail) {
            sendPaymentEmail(userEmail);
        }
    }, [isAlreadyPaid, userEmail]);

    const handleCheckout = () => {
        if (!isAgreed) return;

        // Casting import.meta to any to avoid TS strict errors with custom env vars
        const meta = import.meta as any;
        const env = meta.env || {};

        // --- TEST MODE HANDLING ---
        if (isTestMode) {
            const testPaymentLink = env.VITE_STRIPE_TEST_PAYMENT_LINK;
            
            // 1. If a specific Test Mode Stripe Link is configured, use it.
            // This allows testing with "real" Stripe Test Mode (fake cards).
            if (testPaymentLink && testPaymentLink.startsWith('http')) {
                window.location.href = testPaymentLink;
                return;
            }

            // 2. Fallback: Instant Success without reload
            if (onPaymentSuccess) {
                onPaymentSuccess();
            } else {
                // If callback not provided, fallback to reload method (legacy)
                const currentUrl = new URL(window.location.href);
                currentUrl.searchParams.set('payment_success', 'true');
                window.location.href = currentUrl.toString();
            }
            return;
        }

        // --- PRODUCTION MODE ---
        const paymentLink = env.VITE_STRIPE_PAYMENT_LINK;

        if (!paymentLink) {
            setConfigError(true);
            return;
        }

        // Redirect to Stripe
        window.location.href = paymentLink;
    };

    // --- STATE 1: SUCCESS / RECEIPT ---
    if (isAlreadyPaid) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center relative">
                    <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-700"><XIcon className="w-6 h-6" /></button>
                    <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h2>
                    <p className="text-gray-600 mb-6">An email receipt has been sent to <strong>{userEmail || 'your email address'}</strong>. You now have full access to prepare your applications.</p>
                    <div className="flex flex-col gap-3">
                        <button onClick={onClose} className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg">
                            Continue to Dashboard
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // --- STATE 2: CONFIGURATION ERROR ---
    if (configError) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center relative">
                    <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-700"><XIcon className="w-6 h-6" /></button>
                    
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <XCircleIcon className="w-8 h-8 text-red-600" />
                    </div>
                    
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Configuration Missing: Stripe Payment Link</h2>
                    <p className="text-gray-600 mb-6 text-sm">
                        To accept real payments:
                        <br />1. Create a Payment Link in Stripe.
                        <br />2. Add 'VITE_STRIPE_PAYMENT_LINK' to your Vercel Environment Variables.
                    </p>
                    <div className="mb-4 text-xs text-gray-500 bg-gray-100 p-2 rounded">
                        To keep testing without payment, ensure 'Test Mode' is ON in Settings.
                    </div>

                    <div className="space-y-3">
                        {onPaymentSuccess && (
                            <button 
                                onClick={onPaymentSuccess} 
                                className="bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-2 px-6 rounded-lg w-full"
                            >
                                Simulate Payment (Dev Bypass)
                            </button>
                        )}
                        <button onClick={onClose} className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-2 px-6 rounded-lg w-full">
                            Close
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // --- STATE 3: CHECKOUT ---
    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 relative">
                 {isTestMode && <div className="absolute top-0 left-1/2 -translate-x-1/2 bg-yellow-400 text-yellow-900 font-bold px-4 py-1 rounded-b-lg text-sm">TEST MODE</div>}
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-700"><XIcon className="w-6 h-6" /></button>
                
                <div className="text-center mb-6">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mb-4">
                        <CreditCardIcon className="w-7 h-7 text-blue-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">Upgrade to Full Support</h2>
                    <p className="text-gray-600 mt-2">Unlock application preparation for all eligible programs.</p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg border mb-4">
                    <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-700 font-semibold">ForgivePath Application Pack</span>
                        <span className="text-gray-900 font-bold">$79.00</span>
                    </div>
                    <div className="border-t my-2"></div>
                    <div className="flex justify-between items-center">
                        <span className="text-gray-900 font-bold">Total Due</span>
                        <span className="text-green-600 font-bold text-xl">$79.00</span>
                    </div>
                </div>

                <label className="flex items-start gap-3 p-3 mb-6 bg-blue-50 border border-blue-100 rounded-lg cursor-pointer">
                    <input 
                        type="checkbox" 
                        checked={isAgreed}
                        onChange={(e) => setIsAgreed(e.target.checked)}
                        className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-xs text-gray-600">
                        I acknowledge that ForgivePath is a private document preparation service and <strong>not affiliated with the U.S. Department of Education</strong>. I understand I can file these forms for free at StudentAid.gov. I agree to the Terms of Service.
                    </span>
                </label>

                <button 
                    onClick={handleCheckout} 
                    disabled={!isAgreed}
                    className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-6 rounded-lg shadow-lg transition-all flex items-center justify-center gap-2 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                    <ShieldIcon className="w-5 h-5" />
                    {isTestMode ? 'Proceed to Test Payment' : 'Pay Securely via Stripe'}
                </button>
                
                <div className="mt-4 text-center">
                    <p className="text-xs text-gray-500 flex items-center justify-center gap-1">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                        256-bit SSL Encrypted Payment
                    </p>
                </div>
            </div>
        </div>
    );
};

export default PaymentConfirmationModal;
